import axios from 'axios';
import { getCookie, setCookie, migrateLocalStorage } from './cookies';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

export function getClientId() {
  // migrate legacy localStorage id if present
  migrateLocalStorage({ bc_client_id: 'bc_cid' });
  let id = getCookie('bc_cid');
  if (!id) {
    id = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : 'cid-' + Math.random().toString(36).slice(2) + Date.now();
    setCookie('bc_cid', id, 365);
  }
  return id;
}

// NOTE: same-origin requests (frontend and /api share the origin via the
// ingress) always include cookies automatically. We deliberately DO NOT set
// withCredentials/credentials:'include' here: in this environment credentialed
// requests hang, and they are unnecessary for same-origin cookie auth.
const client = axios.create({ baseURL: API_BASE });
client.interceptors.request.use((cfg) => {
  cfg.headers['X-Client-Id'] = getClientId();
  try {
    // Token may live in localStorage ("stay signed in") OR sessionStorage
    // (this-browser-session only, cleared when the browser closes).
    const token = localStorage.getItem('bcs-token') || sessionStorage.getItem('bcs-token');
    if (token) cfg.headers['Authorization'] = `Bearer ${token}`;
    const lic = localStorage.getItem('bcs-license');
    if (lic) cfg.headers['X-License-Key'] = lic;
  } catch (e) { /* storage unavailable */ }
  return cfg;
});

export const api = {
  getTypes: () => client.get('/barcode/types'),
  generate: (body) => client.post('/barcode/generate', body),
  batch: (body) => client.post('/barcode/batch', body),
  labels: (body) => client.post('/barcode/labels', body),
  getTemplates: () => client.get('/label-templates'),
  saveTemplate: (body) => client.post('/label-templates', body),
  deleteTemplate: (id) => client.delete(`/label-templates/${id}`),
  renameTemplate: (id, name) => client.put(`/label-templates/${id}`, { name }),
  getHistory: (limit = 100) => client.get(`/history?limit=${limit}`),
  deleteHistoryItem: (id) => client.delete(`/history/${id}`),
  clearHistory: () => client.delete('/history'),
  sendContact: (body) => client.post('/contact', body),
  // Share / referral tracking (anonymous)
  recordShare: (body) => client.post('/share', body),
  // Export tracking (anonymous) – feeds the admin conversion funnel
  recordExport: (body) => client.post('/export', body),
  // Reviews
  getReviewSummary: () => client.get('/reviews/summary'),
  // Trustpilot live aggregate rating (public, cached server-side)
  getTrustpilotSummary: (locale = 'de-DE') =>
    client.get(`/trustpilot/summary?locale=${encodeURIComponent(locale)}`),
  getReviews: (limit = 20) => client.get(`/reviews?limit=${limit}`),
  submitReview: (body) => client.post('/reviews', body),
  adminListReviews: (password) =>
    client.get('/reviews/all?limit=500', { headers: { 'X-Admin-Password': password } }),
  adminApproveReview: (id, approved, password) =>
    client.patch(`/reviews/${id}/approve?approved=${approved}`, null, { headers: { 'X-Admin-Password': password } }),
  adminDeleteReview: (id, password) =>
    client.delete(`/reviews/${id}`, { headers: { 'X-Admin-Password': password } }),
  adminGetSpamWords: (password) =>
    client.get('/settings/spam-words', { headers: { 'X-Admin-Password': password } }),
  adminSetSpamWords: (words, password) =>
    client.put('/settings/spam-words', { words }, { headers: { 'X-Admin-Password': password } }),
  adminGetUsernameBlocklist: (password) =>
    client.get('/admin/username-blocklist', { headers: { 'X-Admin-Password': password } }),
  adminAddUsernameBlocklist: (username, password) =>
    client.post('/admin/username-blocklist', { username }, { headers: { 'X-Admin-Password': password } }),
  adminRemoveUsernameBlocklist: (username, password) =>
    client.delete(`/admin/username-blocklist/${encodeURIComponent(username)}`, { headers: { 'X-Admin-Password': password } }),
  adminBulkUsernameBlocklist: (text, password) =>
    client.post('/admin/username-blocklist/bulk', { text }, { headers: { 'X-Admin-Password': password } }),
  adminGetReservedUsernames: (password) =>
    client.get('/admin/reserved-usernames', { headers: { 'X-Admin-Password': password } }),
  adminReleaseReservedUsername: (username, password) =>
    client.delete(`/admin/reserved-usernames/${encodeURIComponent(username)}`, { headers: { 'X-Admin-Password': password } }),
  adminBulkReleaseReserved: (usernames, password) =>
    client.post('/admin/reserved-usernames/release', { usernames }, { headers: { 'X-Admin-Password': password } }),
  adminGetAvatarModerationLog: (password) =>
    client.get('/admin/avatar-moderation-log', { headers: { 'X-Admin-Password': password } }),
  adminBypassAvatarModeration: (userId, password) =>
    client.post('/admin/avatar-moderation-bypass', { user_id: userId }, { headers: { 'X-Admin-Password': password } }),
  adminExtendReservedUsername: (username, password) =>
    client.post('/admin/reserved-usernames/extend', { username }, { headers: { 'X-Admin-Password': password } }),
  // Maintenance mode (public status + admin toggle)
  getMaintenance: () => client.get('/maintenance'),
  adminSetMaintenance: (body, password) =>
    client.put('/maintenance', body, { headers: { 'X-Admin-Password': password } }),
  adminTestEmail: (password) =>
    client.post('/admin/email-test', null, { headers: { 'X-Admin-Password': password } }),
  adminEmailLog: (password, limit = 50, status = '', kind = '', dateFrom = '', dateTo = '') => {
    const params = { limit };
    if (status) params.status = status;
    if (kind) params.kind = kind;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return client.get('/admin/email-log', { params, headers: { 'X-Admin-Password': password } });
  },
  adminEmailResend: (password, id) =>
    client.post('/admin/email-resend', { id }, { headers: { 'X-Admin-Password': password } }),
  adminExportEmailLog: (password, status = '', kind = '', dateFrom = '', dateTo = '') => {
    const params = {};
    if (status) params.status = status;
    if (kind) params.kind = kind;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return client.get('/admin/email-log/export', { params, responseType: 'blob', headers: { 'X-Admin-Password': password } });
  },
  adminLockedUsers: (password) =>
    client.get('/admin/locked-users', { headers: { 'X-Admin-Password': password } }),
  adminUnlockUser: (password, body) =>
    client.post('/admin/users/unlock', body, { headers: { 'X-Admin-Password': password } }),
  adminGetSecuritySettings: (password) =>
    client.get('/admin/security-settings', { headers: { 'X-Admin-Password': password } }),
  adminSetSecuritySettings: (password, body) =>
    client.put('/admin/security-settings', body, { headers: { 'X-Admin-Password': password } }),
  adminEmailPreview: (password) =>
    client.get('/admin/email-preview', { headers: { 'X-Admin-Password': password } }),
  adminGetEmailSettings: (password) =>
    client.get('/admin/email-settings', { headers: { 'X-Admin-Password': password } }),
  adminSetEmailSettings: (body, password) =>
    client.put('/admin/email-settings', body, { headers: { 'X-Admin-Password': password } }),
  // Custom SMTP delivery (admin)
  adminGetSmtpSettings: (password) =>
    client.get('/admin/smtp-settings', { headers: { 'X-Admin-Password': password } }),
  adminSetSmtpSettings: (password, body) =>
    client.put('/admin/smtp-settings', body, { headers: { 'X-Admin-Password': password } }),
  adminSmtpTest: (password, to) =>
    client.post('/admin/smtp-test', { to }, { headers: { 'X-Admin-Password': password } }),
  adminSmtpStatus: (password) =>
    client.get('/admin/smtp-status', { headers: { 'X-Admin-Password': password } }),
  adminSmtpHistory: (password, limit = 8) =>
    client.get('/admin/smtp-history', { params: { limit }, headers: { 'X-Admin-Password': password } }),
  // Premium sales config (public read + admin toggle)
  getPremiumConfig: () => client.get('/premium/config'),
  adminSetPremiumConfig: (body, password) =>
    client.put('/premium/config', body, { headers: { 'X-Admin-Password': password } }),
  // Admin statistics (KPI overview)
  adminStats: (password) =>
    client.get('/admin/stats', { headers: { 'X-Admin-Password': password } }),
  adminUsage: (password, days = 30) =>
    client.get(`/admin/usage?days=${days}`, { headers: { 'X-Admin-Password': password } }),
  adminSharesTrend: (password, days = 30) =>
    client.get(`/admin/shares/trend?days=${days}`, { headers: { 'X-Admin-Password': password } }),
  adminBookClicksTrend: (password, days = 30) =>
    client.get(`/admin/book-clicks/trend?days=${days}`, { headers: { 'X-Admin-Password': password } }),
  adminFunnel: (password, days = 30) =>
    client.get(`/admin/funnel?days=${days}`, { headers: { 'X-Admin-Password': password } }),
  // Bulk admin actions
  adminBulkReviews: (ids, action, password) =>
    client.post('/reviews/bulk', { ids, action }, { headers: { 'X-Admin-Password': password } }),
  adminBulkContact: (ids, action, password) =>
    client.post('/contact/bulk', { ids, action }, { headers: { 'X-Admin-Password': password } }),
  // Admin security: locked-out IP management
  adminLockedIps: (password) =>
    client.get('/admin/locked-ips', { headers: { 'X-Admin-Password': password } }),
  adminUnlockIp: (ip, password) =>
    client.post('/admin/unlock-ip', { ip }, { headers: { 'X-Admin-Password': password } }),
  adminUnlockAllIps: (password) =>
    client.post('/admin/unlock-ip', { all: true }, { headers: { 'X-Admin-Password': password } }),
  // Admin security: login-origin (user login) IP lockouts
  adminLockedLoginIps: (password) =>
    client.get('/admin/locked-login-ips', { headers: { 'X-Admin-Password': password } }),
  adminUnlockLoginIp: (ip, password) =>
    client.post('/admin/unlock-login-ip', { ip }, { headers: { 'X-Admin-Password': password } }),
  adminUnlockAllLoginIps: (password) =>
    client.post('/admin/unlock-login-ip', { all: true }, { headers: { 'X-Admin-Password': password } }),
  // Generator presets (favorites), per client_id
  getPresets: () => client.get('/presets'),
  savePreset: (body) => client.post('/presets', body),
  deletePreset: (id) => client.delete(`/presets/${id}`),
  // --- Paywall / auth / billing (feature-flagged; inert while disabled) ---
  getConfig: () => client.get('/config'),
  register: (body) => client.post('/auth/register', body),
  login: (body) => client.post('/auth/login', body),
  // Emergent-managed Google OAuth: exchange the one-time session_id for a session.
  createSession: (sessionId, remember = true) => client.post('/auth/session', null, { headers: { 'X-Session-ID': sessionId, 'X-Remember': remember ? '1' : '0' } }),
  me: () => client.get('/auth/me'),
  getCaptcha: () => client.get('/auth/captcha'),
  loginHistory: (limit = 20) => client.get('/auth/login-history', { params: { limit } }),
  loginHistoryExport: () => client.get('/auth/login-history/export', { responseType: 'blob' }),
  revokeOtherSessions: () => client.post('/auth/sessions/revoke-others', {}),
  listSessions: () => client.get('/auth/sessions'),
  revokeSession: (id) => client.post('/auth/sessions/revoke', { id }),
  adminSecurityLog: (password, limit = 50, kind = '') =>
    client.get('/admin/security-log', { params: kind ? { limit, kind } : { limit }, headers: { 'X-Admin-Password': password } }),
  updateProfile: (body) => client.put('/auth/profile', body),
  setUsername: (username) => client.post('/auth/account/username', { username }),
  checkUsername: (username) => client.get('/auth/username-check', { params: { username } }),
  uploadAvatar: (file) => {
    const fd = new FormData();
    fd.append('file', file, (file && file.name) || 'avatar.jpg');
    return client.post('/auth/account/avatar', fd);
  },
  deleteAvatar: () => client.delete('/auth/account/avatar'),
  setAvatarColor: (color) => client.post('/auth/account/avatar-color', { color }),
  forgotPassword: (identifier) => client.post('/auth/password/forgot', { identifier }),
  resetPassword: (body) => client.post('/auth/password/reset', body),
  setEmailPrefs: (prefs) => client.post('/auth/account/email-prefs',
    (typeof prefs === 'object' && prefs !== null) ? prefs : { email_reminders_enabled: prefs }),
  getMyDonations: () => client.get('/auth/account/donations'),
  changePassword: (body) => client.post('/auth/change-password', body),
  deleteAccount: () => client.delete('/auth/account'),
  scheduleAccountDeletion: () => client.post('/auth/account/schedule-deletion'),
  cancelScheduledDeletion: () => client.delete('/auth/account/schedule-deletion'),
  // Free daily quota (server-side, IP-based)
  getQuota: () => client.get('/usage/quota'),
  consumeQuota: () => client.post('/usage/consume'),
  // Stripe payments
  createCheckout: (body) => client.post('/payments/checkout', body),
  paymentStatus: (sessionId) => client.get(`/payments/status/${sessionId}`),
  cancelSubscription: () => client.post('/payments/cancel'),
  reactivateSubscription: () => client.post('/payments/reactivate'),
  paymentHistory: () => client.get('/payments/history'),
  paymentReceipt: (id) => client.get(`/payments/${id}/receipt`, { responseType: 'blob' }),
  // Generation points / credits
  getPoints: () => client.get('/points'),
  consumePoints: (amount = 1) => client.post('/points/consume', { amount }),
  getPointsPacks: () => client.get('/points/packs'),
  pointsHistory: () => client.get('/points/history'),
  // Label paper templates (brand + product number -> grid template, live)
  getLabelBrands: () => client.get('/labels/brands'),
  lookupLabel: (brand, code) => client.get('/labels/lookup', { params: { brand, code } }),
  labelParts: (brand, q = '', limit = 12) => client.get('/labels/parts', { params: { brand, q, limit } }),
  adminGetFreeQuota: (password) =>
    client.get('/admin/free-quota', { headers: { 'X-Admin-Password': password } }),
  adminSetFreeQuota: (limit, password) =>
    client.put('/admin/free-quota', { limit }, { headers: { 'X-Admin-Password': password } }),
  logout: () => client.post('/auth/logout'),
  getEntitlement: () => client.get('/billing/entitlement'),
  getPlans: () => client.get('/billing/plans'),
  startTrial: (plan = 'monthly') => client.post('/billing/trial/start', { plan }),
  redeemLicense: (key) => client.post('/billing/license/redeem', { key }),
  checkout: (plan) => client.post('/billing/checkout', { plan }),
  adminSubscriptions: (password) =>
    client.get('/admin/subscriptions', { headers: { 'X-Admin-Password': password } }),
  adminDonations: (password, limit = 500) =>
    client.get('/admin/donations', { params: { limit }, headers: { 'X-Admin-Password': password } }),
  adminListLicenses: (password) =>
    client.get('/admin/licenses', { headers: { 'X-Admin-Password': password } }),
  adminLicensesTrend: (password, weeks = 8) =>
    client.get('/admin/licenses/trend', { params: { weeks }, headers: { 'X-Admin-Password': password } }),
  adminReviewBonusUsage: (password, onlyCapped = 0) =>
    client.get('/admin/review-bonus-usage', { params: { only_capped: onlyCapped }, headers: { 'X-Admin-Password': password } }),
  adminReviewBonusHistory: (password, userId) =>
    client.get('/admin/review-bonus-history', { params: { user_id: userId }, headers: { 'X-Admin-Password': password } }),
  adminReviewBonusRevoke: (password, userId, amount, reason) =>
    client.post('/admin/review-bonus-revoke', { user_id: userId, amount, reason }, { headers: { 'X-Admin-Password': password } }),
  adminLoginCountryStats: (password, days = 30) =>
    client.get('/admin/login-country-stats', { params: { days }, headers: { 'X-Admin-Password': password } }),
  adminCreateLicense: (body, password) =>
    client.post('/admin/licenses', body, { headers: { 'X-Admin-Password': password } }),
  adminRevokeLicense: (key, password) =>
    client.post(`/admin/licenses/${key}/revoke`, null, { headers: { 'X-Admin-Password': password } }),
  // Admin (password passed per call via X-Admin-Password header)
  adminListContact: (password, limit = 200) =>
    client.get(`/contact?limit=${limit}`, { headers: { 'X-Admin-Password': password } }),
  adminSetHandled: (id, handled, password) =>
    client.patch(`/contact/${id}?handled=${handled}`, null, { headers: { 'X-Admin-Password': password } }),
  adminDeleteContact: (id, password) =>
    client.delete(`/contact/${id}`, { headers: { 'X-Admin-Password': password } }),
};

export default api;
